/**
 * AI Workflow Visualization Component
 *
 * Interactive graph visualization for AI workflow decomposition.
 * Features:
 * - Automatic layout of workflow nodes
 * - Real-time status updates during execution
 * - Interactive node controls (approve, reject, re-run)
 * - Execution mode controls (step-through, run-until-input)
 * - Cost tracking display
 * - Workflow interruption support
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
  ReactFlowProvider
} from 'reactflow';
import 'reactflow/dist/style.css';
import axios from 'axios';

import { aiWorkflowNodeTypes, getNodeType } from './nodes/AIWorkflowNodes';
import WorkflowProgressSummary from './WorkflowProgressSummary';
import AIWorkflowNodeEditModal from './AIWorkflowNodeEditModal';
import AIWorkflowNodeApprovalModal from './AIWorkflowNodeApprovalModal';
import ContextMenu from './ContextMenu';
import ExecutionInputForm from './ExecutionInputForm';
import FilePicker from './FilePicker';
import FilesViewer from './FilesViewer';
import { smartUpload, downloadFile, listWorkflowFiles } from '../api/filesApi';
import { saveWorkflowAsTemplate } from '../api/templatesApi';
import {
  useAIWorkflowStatus,
  useAIWorkflowExecution,
  useAIWorkflowRerun,
  useAIWorkflowApproval,
  useAIWorkflowCost,
  useAIWorkflowTransition
} from '../hooks/useAIWorkflow';
import { useAIWorkflowNodeUpdate } from '../hooks/useAIWorkflowNodeUpdate';
import { useSSE } from '../hooks/useSSE';
import {
  getWorkflowIdentifier,
  formatWorkflowIdentifier,
  generateIntelligentFilename
} from '../utils/workflowIdentifier';

import {
  Play,
  Pause,
  StopCircle,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  XCircle,
  FastForward,
  Footprints,
  Maximize2,
  Minimize2,
  Clock,
  Loader,
  Paperclip,
  Square,
  MinusCircle,
  Zap,
  Download,
  Save,
  Tag,
  Edit,
  Trash2,
  Bookmark
} from 'lucide-react';

/**
 * Convert API graph format to React Flow format
 */
function convertGraphToReactFlow(graph) {
  if (!graph || !graph.nodes || !graph.edges) {
    return { nodes: [], edges: [] };
  }

  // Convert nodes - use getNodeType for fallback to 'default' on unknown types
  const nodes = graph.nodes.map((node) => ({
    id: node.id,
    type: getNodeType(node.node_type),
    position: calculateNodePosition(node, graph.nodes),
    data: {
      nodeKey: node.node_key,
      displayName: node.display_name,
      description: node.description,
      nodeType: node.node_type,
      status: node.status,
      complexity: node.complexity_score,
      requiresApproval: node.requires_approval,
      approvalPrompt: node.approval_prompt,
      depthLevel: node.depth_level,
      orderIndex: node.order_index,
      cost: node.cost,
      tokensInput: node.tokens_input,
      tokensOutput: node.tokens_output,
      humanOutput: node.human_output,
      error: node.error,
      // Additional fields for editing
      prompt_template: node.prompt_template,
      system_prompt: node.system_prompt,
      selected_model: node.selected_model,
      estimatedTokens: node.estimated_tokens
    }
  }));

  // Convert edges with enhanced styling based on type
  const edges = graph.edges.map((edge) => {
    const isSequential = edge.edge_type === 'sequential';
    const isConditional = edge.edge_type === 'conditional';
    const isReconciliation = edge.edge_type === 'reconciliation';

    return {
      id: edge.id,
      source: edge.from_node_id,
      target: edge.to_node_id,
      type: isSequential ? 'default' : 'step',
      animated: isSequential,
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: isConditional ? '#F59E0B' : isReconciliation ? '#8B5CF6' : '#6B7280'
      },
      style: {
        strokeWidth: isSequential ? 3 : 2,
        stroke: isConditional ? '#F59E0B' : isReconciliation ? '#8B5CF6' : '#6B7280',
        strokeDasharray: isConditional ? '5,5' : undefined
      }
    };
  });

  return { nodes, edges };
}

/**
 * Check if node data has actually changed to avoid unnecessary object creation
 */
function hasNodeDataChanged(currentNode, statusNode) {
  if (!statusNode) return false;

  const current = currentNode.data;
  return (
    current.status !== statusNode.status ||
    current.cost !== statusNode.cost ||
    current.tokensInput !== statusNode.tokens_input ||
    current.tokensOutput !== statusNode.tokens_output ||
    current.humanOutput !== statusNode.human_output ||
    current.error !== statusNode.error ||
    current.displayName !== statusNode.display_name ||
    current.description !== statusNode.description ||
    current.complexity !== statusNode.complexity_score
  );
}

/**
 * Calculate node position based on depth and order
 * Uses a tree layout algorithm
 */
function calculateNodePosition(node, allNodes) {
  const horizontalSpacing = 350;
  const verticalSpacing = 200;

  // Sink nodes (reconciliation/consolidation) are assigned depth_level 0 by the
  // decomposer but should render below all other nodes. Detect them by node_type
  // and place them at max_depth + 1.
  const SINK_TYPES = new Set(['reconciliation', 'consolidation', 'output']);
  const isSink = SINK_TYPES.has(node.node_type);

  const maxDepth = allNodes.reduce((m, n) => {
    const d = SINK_TYPES.has(n.node_type) ? 0 : (n.depth_level || 0);
    return Math.max(m, d);
  }, 0);

  const effectiveDepth = isSink ? maxDepth + 1 : (node.depth_level || 0);

  // Group nodes by effective depth level
  const nodesByDepth = {};
  allNodes.forEach((n) => {
    const d = SINK_TYPES.has(n.node_type) ? maxDepth + 1 : (n.depth_level || 0);
    if (!nodesByDepth[d]) nodesByDepth[d] = [];
    nodesByDepth[d].push(n);
  });

  // Sort nodes at each depth by order_index
  Object.keys(nodesByDepth).forEach((depth) => {
    nodesByDepth[depth].sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
  });

  const nodesAtDepth = nodesByDepth[effectiveDepth] || [];
  const indexAtDepth = nodesAtDepth.findIndex((n) => n.id === node.id);

  // Center nodes horizontally based on count at this depth
  const totalWidth = nodesAtDepth.length * horizontalSpacing;
  const startX = -totalWidth / 2;

  return {
    x: startX + indexAtDepth * horizontalSpacing,
    y: effectiveDepth * verticalSpacing
  };
}

/**
 * Execution mode selector
 */
function ExecutionModeSelector({ value, onChange }) {
  const modes = [
    {
      value: 'step_through',
      label: 'Step Through',
      icon: Footprints,
      description: 'Pause after each node'
    },
    {
      value: 'run_until_input',
      label: 'Run Until Input',
      icon: FastForward,
      description: 'Auto-run until approval needed'
    }
  ];

  return (
    <div className="bg-white rounded-lg shadow p-4 mb-4">
      <div className="text-sm font-medium text-gray-700 mb-2">Execution Mode</div>
      <div className="flex gap-2">
        {modes.map((mode) => {
          const Icon = mode.icon;
          const isActive = value === mode.value;

          return (
            <button
              key={mode.value}
              onClick={() => onChange(mode.value)}
              className={`flex-1 p-3 rounded-lg border-2 transition-all ${isActive
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
                }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon size={16} className={isActive ? 'text-blue-600' : 'text-gray-600'} />
                <span
                  className={`text-sm font-medium ${isActive ? 'text-blue-600' : 'text-gray-700'
                    }`}
                >
                  {mode.label}
                </span>
              </div>
              <div className="text-xs text-gray-500">{mode.description}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Workflow control panel
 */
function ControlPanel({
  workflow,
  status,
  executionMode,
  onExecutionModeChange,
  onExecute,
  onResume,
  onPause,
  onInterrupt,
  executing,
  paused,
  onStatusToggle,
  transitioning,
  onRetryDecomposition,
  retryingDecomposition
}) {
  const [interruptInput, setInterruptInput] = useState('');
  const [showInterruptDialog, setShowInterruptDialog] = useState(false);

  const handleInterrupt = () => {
    if (interruptInput.trim()) {
      onInterrupt(interruptInput);
      setInterruptInput('');
      setShowInterruptDialog(false);
    }
  };

  const workflowStatus = status?.status || workflow?.status || 'draft';
  const pendingApprovalCount = status?.pending_approval_nodes || 0;

  // Determine status display color and message
  const getStatusDisplay = () => {
    if (workflowStatus === 'paused' && pendingApprovalCount > 0) {
      return {
        color: 'text-orange-600',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-300',
        message: `Waiting for ${pendingApprovalCount} approval${pendingApprovalCount > 1 ? 's' : ''}`,
        icon: AlertTriangle
      };
    }
    if (workflowStatus === 'failed') {
      return {
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-300',
        message: 'Failed',
        icon: XCircle
      };
    }
    if (workflowStatus === 'executing') {
      return {
        color: 'text-blue-600',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-300',
        message: 'Running',
        icon: Loader
      };
    }
    if (workflowStatus === 'paused') {
      return {
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-300',
        message: 'Paused',
        icon: Pause
      };
    }
    return {
      color: 'text-gray-600',
      bgColor: 'bg-gray-50',
      borderColor: 'border-gray-300',
      message: workflowStatus,
      icon: Clock
    };
  };

  const statusDisplay = getStatusDisplay();
  const StatusIcon = statusDisplay.icon;

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Workflow Control</h3>
          {/* Show toggle button for draft/ready status */}
          {(workflowStatus === 'draft' || workflowStatus === 'ready') ? (
            <button
              onClick={onStatusToggle}
              disabled={transitioning}
              className={`text-sm mt-2 px-3 py-2 rounded-lg border-2 ${statusDisplay.bgColor} ${statusDisplay.borderColor} flex items-center gap-2 hover:opacity-80 transition-opacity disabled:opacity-50`}
              title={`Click to switch to ${workflowStatus === 'draft' ? 'ready' : 'draft'} status`}
            >
              <StatusIcon size={16} className={statusDisplay.color} />
              <span className={`font-semibold ${statusDisplay.color}`}>
                Status: {transitioning ? 'Updating...' : statusDisplay.message}
              </span>
              <Edit size={14} className={`${statusDisplay.color} ml-1`} />
            </button>
          ) : (
            <div className={`text-sm mt-2 px-3 py-2 rounded-lg border-2 ${statusDisplay.bgColor} ${statusDisplay.borderColor} flex items-center gap-2`}>
              <StatusIcon size={16} className={`${statusDisplay.color} ${workflowStatus === 'executing' ? 'animate-spin' : ''}`} />
              <span className={`font-semibold ${statusDisplay.color}`}>
                Status: {statusDisplay.message}
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          {workflowStatus === 'draft' || workflowStatus === 'ready' ? (
            <button
              onClick={onExecute}
              disabled={executing}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              <Play size={16} />
              <span>{executing ? 'Starting...' : 'Start Execution'}</span>
            </button>
          ) : null}

          {workflowStatus === 'paused' ? (
            <button
              onClick={onResume}
              disabled={executing}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              <Play size={16} />
              <span>{executing ? 'Resuming...' : 'Resume'}</span>
            </button>
          ) : null}

          {(workflowStatus === 'executing' || workflowStatus === 'paused') && (
            <>
              <button
                onClick={onPause}
                className="flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
              >
                <Pause size={16} />
                <span>Pause</span>
              </button>
              <button
                onClick={() => setShowInterruptDialog(true)}
                className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
              >
                <StopCircle size={16} />
                <span>Interrupt</span>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Interrupt dialog */}
      {showInterruptDialog && (
        <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
          <div className="flex items-center gap-2 text-orange-700 font-medium mb-2">
            <AlertTriangle size={16} />
            <span>Interrupt Workflow</span>
          </div>
          <textarea
            value={interruptInput}
            onChange={(e) => setInterruptInput(e.target.value)}
            placeholder="Enter new instructions to inject into workflow..."
            className="w-full p-3 border border-orange-200 rounded-lg text-sm resize-none"
            rows={3}
          />
          <div className="flex gap-2 mt-2">
            <button
              onClick={handleInterrupt}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm"
            >
              Inject Instructions
            </button>
            <button
              onClick={() => {
                setShowInterruptDialog(false);
                setInterruptInput('');
              }}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Failed workflow error banner */}
      {workflowStatus === 'failed' && status?.error_message && (() => {
        const errorMsg = status.error_message;
        const depthMatch = errorMsg.match(/depth (\d+) exceeds tier limit of (\d+)/);
        const isDepthError = !!depthMatch;
        const tierLimit = depthMatch ? parseInt(depthMatch[2], 10) : null;

        return (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-2 text-red-700 mb-2">
              <XCircle size={18} className="mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-medium">Decomposition Failed</div>
                <div className="text-sm mt-1 text-red-600">{errorMsg}</div>
              </div>
            </div>
            {isDepthError && onRetryDecomposition && (
              <div className="mt-3 flex items-center gap-3">
                <button
                  onClick={() => onRetryDecomposition(tierLimit)}
                  disabled={retryingDecomposition}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors text-sm"
                >
                  <RefreshCw size={14} className={retryingDecomposition ? 'animate-spin' : ''} />
                  <span>{retryingDecomposition ? 'Retrying...' : `Retry with shallower decomposition (max depth ${tierLimit})`}</span>
                </button>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

/**
 * Cost tracker panel
 */
const NODE_TYPE_BADGES = {
  llm_analysis:      { label: 'Analysis',     color: 'bg-purple-100 text-purple-700' },
  tool_execution:    { label: 'Tool',          color: 'bg-blue-100 text-blue-700' },
  pre_priced_tool:   { label: 'Tool',          color: 'bg-blue-100 text-blue-700' },
  reconciliation:    { label: 'Reconcile',     color: 'bg-gray-100 text-gray-600' },
  agentic:           { label: 'Agentic',       color: 'bg-orange-100 text-orange-700' },
  human_input:       { label: 'Human Input',   color: 'bg-yellow-100 text-yellow-700' },
};

function NodeTypeSummaryPanel({ costData, loading, nodes }) {
  // Derive live counts from nodes state (SSE-updated) — don't rely on stale costData
  const liveNodeCount = nodes ? nodes.length : 0;
  const liveCompletedNodes = nodes ? nodes.filter(n => n.data?.status === 'completed').length : 0;
  const completionPercentage = liveNodeCount > 0 ? (liveCompletedNodes / liveNodeCount) * 100 : 0;

  // Node type breakdown still comes from costData (static, set at decomposition time)
  const breakdown_by_node_type = costData?.breakdown_by_node_type;

  if (liveNodeCount === 0) return null;

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="mb-3">
        <div className="text-xs text-gray-500 mb-1">
          {liveCompletedNodes} / {liveNodeCount} nodes complete
        </div>
        <div className="w-full bg-gray-200 rounded-full h-1.5">
          <div
            className="bg-indigo-500 h-1.5 rounded-full transition-all"
            style={{ width: `${completionPercentage}%` }}
          />
        </div>
      </div>

      {breakdown_by_node_type && Object.keys(breakdown_by_node_type).length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {Object.entries(breakdown_by_node_type).map(([type, count]) => {
            const badge = NODE_TYPE_BADGES[type] || { label: type.replace(/_/g, ' '), color: 'bg-gray-100 text-gray-600' };
            return (
              <span key={type} className={`text-xs px-2 py-0.5 rounded-full font-medium ${badge.color}`}>
                {badge.label}{count > 1 ? ` ×${count}` : ''}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Collapsible real-time event log panel (dev/local use).
 * Subscribes to all SSE events via wildcard listener and renders them
 * as a scrollable log with colour-coded severity.
 */
function WorkflowEventLog({ workflowId }) {
  const { subscribe } = useSSE();
  const [entries, setEntries] = useState([]);
  const [open, setOpen] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    const unsubAll = subscribe('*', ({ type, data }) => {
      // Only show events for this workflow (or events with no workflow_id)
      if (data?.workflow_id && data.workflow_id !== String(workflowId)) return;
      setEntries(prev => {
        const next = [...prev, { type, data, ts: new Date() }];
        return next.length > 200 ? next.slice(-200) : next;
      });
    });
    return unsubAll;
  }, [workflowId, subscribe]);

  // Auto-scroll when open
  useEffect(() => {
    if (open && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [entries, open]);

  const levelColor = (type) => {
    if (type === 'node.failed' || type === 'workflow.failed') return 'text-red-400';
    if (type === 'node.completed' || type === 'workflow.completed') return 'text-green-400';
    if (type === 'node.started') return 'text-blue-400';
    if (type === 'snapshot') return 'text-yellow-400';
    return 'text-gray-400';
  };

  const fmt = (d) => d.toLocaleTimeString('en-GB', { hour12: false, fractionalSecondDigits: 1 });

  return (
    <div className="mx-4 mb-4 border border-gray-200 rounded-lg overflow-hidden shadow-sm">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-2 bg-gray-800 text-gray-100 text-xs font-mono hover:bg-gray-700 transition-colors"
      >
        <span className="flex items-center gap-2">
          <span className="text-green-400">▶</span>
          <span>Event Log</span>
          {entries.length > 0 && (
            <span className="bg-gray-600 px-1.5 py-0.5 rounded text-gray-300">{entries.length}</span>
          )}
        </span>
        <span className="text-gray-400">{open ? '▲ hide' : '▼ show'}</span>
      </button>

      {open && (
        <div className="bg-gray-900 text-xs font-mono overflow-y-auto max-h-64 p-2 space-y-0.5">
          {entries.length === 0 ? (
            <div className="text-gray-500 italic px-2 py-1">No events yet — run the workflow to see live updates.</div>
          ) : (
            entries.map((e, i) => (
              <div key={i} className="flex items-start gap-2 hover:bg-gray-800 px-1 rounded">
                <span className="text-gray-500 flex-shrink-0">{fmt(e.ts)}</span>
                <span className={`flex-shrink-0 w-36 ${levelColor(e.type)}`}>{e.type}</span>
                <span className="text-gray-300 truncate">
                  {e.data?.node_id
                    ? `[${e.data.node_id?.slice(0, 8)}] ${e.data.status || ''} ${e.data.error || ''}`
                    : e.data?.status || JSON.stringify(e.data).slice(0, 120)}
                </span>
              </div>
            ))
          )}
          <div ref={bottomRef} />
        </div>
      )}
    </div>
  );
}

/**
 * Main AI Workflow Visualization Component
 */
function AIWorkflowVisualizationInner({ workflowId, initialGraph, initialWorkflow }) {
  const [executionMode, setExecutionMode] = useState('run_until_input');  // Changed from step_through to run_until_input
  const [selectedNode, setSelectedNode] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [editingNode, setEditingNode] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [approvingNode, setApprovingNode] = useState(null);
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
  const [approvalNotifications, setApprovalNotifications] = useState([]);
  const [contextMenu, setContextMenu] = useState(null);
  const [contextSelectedNode, setContextSelectedNode] = useState(null);
  const [showFilePicker, setShowFilePicker] = useState(false);
  const [showFilesViewer, setShowFilesViewer] = useState(false);
  const [showWorkflowFiles, setShowWorkflowFiles] = useState(false);
  const [workflowFiles, setWorkflowFiles] = useState([]);
  const [workflowFilesLoading, setWorkflowFilesLoading] = useState(false);
  const [autoApprove, setAutoApprove] = useState(false);
  const [toastNotifications, setToastNotifications] = useState([]);
  const [retryingDecomposition, setRetryingDecomposition] = useState(false);
  const [showSaveAsTemplateModal, setShowSaveAsTemplateModal] = useState(false);
  const [saveTemplateLoading, setSaveTemplateLoading] = useState(false);
  const [saveTemplateError, setSaveTemplateError] = useState(null);
  const [saveTemplateName, setSaveTemplateName] = useState('');
  const [saveTemplateDescription, setSaveTemplateDescription] = useState('');

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  const reactFlowWrapper = useRef(null);
  const reactFlowInstance = useRef(null);

  // Track if we've loaded the initial graph to prevent re-loading
  const initialGraphLoadedRef = useRef(null);
  // Track nodes waiting for approval to show notifications
  const waitingNodesRef = useRef(new Set());
  // Track notification timeouts for cleanup
  const notificationTimeoutsRef = useRef([]);

  // Show a toast notification (auto-dismisses after duration)
  const showToast = useCallback((message, type = 'success', duration = 5000, extra = {}) => {
    const id = Date.now() + Math.random();
    const toast = { id, message, type, ...extra };
    setToastNotifications(prev => [...prev, toast]);
    const timeoutId = setTimeout(() => {
      setToastNotifications(prev => prev.filter(t => t.id !== id));
    }, duration);
    notificationTimeoutsRef.current.push(timeoutId);
    return id;
  }, []);

  const dismissToast = useCallback((id) => {
    setToastNotifications(prev => prev.filter(t => t.id !== id));
  }, []);

  // Hooks for workflow operations
  const { status, setStatus, polling, startPolling, stopPolling, refetch: refetchStatus } = useAIWorkflowStatus(
    workflowId,
    { pollInterval: 2000, autoPoll: false }
  );

  // Live workflow status — SSE/polling takes precedence over static initialWorkflow
  const workflowStatus = status?.status || initialWorkflow?.status || 'draft';

  const {
    executing,
    paused,
    error: executionError,
    execute,
    resume,
    pause,
    interrupt,
    requiredInputs,
    clearRequiredInputs
  } = useAIWorkflowExecution(workflowId);

  const { costData, loading: costLoading, refetch: refetchCost } = useAIWorkflowCost(
    workflowId,
    true
  );

  const { rerun } = useAIWorkflowRerun(workflowId, selectedNode);
  const { approve, reject } = useAIWorkflowApproval(workflowId, selectedNode);
  const { updateNode } = useAIWorkflowNodeUpdate(workflowId);
  const { transition, transitioning } = useAIWorkflowTransition(workflowId);

  // Get workflow identifier
  const workflowIdentifier = useMemo(() => {
    return getWorkflowIdentifier(initialWorkflow);
  }, [initialWorkflow]);

  // Memoize handler functions so they have stable references (defined AFTER hooks)
  const handleNodeSelect = useCallback((nodeKey) => {
    // console.log('Node clicked:', nodeKey);
    setSelectedNode(nodeKey);

    // Find the clicked node from CURRENT nodes state (not stale parameter)
    setNodes((currentNodes) => {
      const clickedNode = currentNodes.find(n => n.data?.nodeKey === nodeKey);
      // console.log('Found clicked node:', clickedNode);

      if (clickedNode) {
        // If node is waiting for approval, open approval modal
        if (clickedNode.data.status === 'waiting_approval') {
          // console.log('Opening approval modal for node:', nodeKey);
          setApprovingNode(clickedNode);
          setIsApprovalModalOpen(true);
        } else {
          // Otherwise open edit modal (for pending/ready nodes)
          // console.log('Opening edit modal for node:', nodeKey);
          setEditingNode(clickedNode);
          setIsEditModalOpen(true);
        }
      } else {
        // console.warn('Could not find node with nodeKey:', nodeKey);
      }

      // Return nodes unchanged
      return currentNodes;
    });
  }, [setNodes]);  // Depend on setNodes (stable from useState)

  const handleNodeApprove = useCallback((nodeKey) => {
    setSelectedNode(nodeKey);
    approve(true);
  }, [approve]);

  const handleNodeReject = useCallback((nodeKey) => {
    setSelectedNode(nodeKey);
    reject('Rejected by user');
  }, [reject]);

  const handleNodeRerun = useCallback((nodeKey) => {
    setSelectedNode(nodeKey);
    rerun();
  }, [rerun]);

  // Toggle workflow status between draft and ready
  const handleStatusToggle = useCallback(async () => {
    const currentStatus = status?.status || initialWorkflow?.status || 'draft';
    const targetStatus = currentStatus === 'draft' ? 'ready' : 'draft';

    const result = await transition(targetStatus);
    if (result) {
      // Refresh status after transition
      refetchStatus();
    }
  }, [status, initialWorkflow?.status, transition, refetchStatus]);

  // Check if node requires simple approval (just "yes" or "continue")
  const isSimpleApprovalNode = useCallback((node) => {
    const prompt = (node.data?.approvalPrompt || node.data?.approval_prompt || '').toLowerCase();
    const simpleKeywords = [
      'continue',
      'proceed',
      'yes',
      'confirm',
      'ready to',
      'click to continue',
      'press to proceed',
      'approve to continue'
    ];

    return simpleKeywords.some(keyword => prompt.includes(keyword));
  }, []);

  // Update nodes and edges when initialGraph changes (e.g., loading from history)
  // Only load once per workflow to prevent infinite loops
  useEffect(() => {
    if (initialGraph && initialGraph.nodes && initialGraph.edges) {
      // Check if we've already loaded this workflow
      if (initialGraphLoadedRef.current === workflowId) {
        return;
      }

      const flowData = convertGraphToReactFlow(initialGraph);

      // Attach handlers to initial nodes
      // Note: We use inline functions for save/download to avoid forward reference issues
      const nodesWithHandlers = flowData.nodes.map(node => ({
        ...node,
        data: {
          ...node.data,
          onApprove: handleNodeApprove,
          onReject: handleNodeReject,
          onRerun: handleNodeRerun,
          onSelect: handleNodeSelect,  // No longer pass stale nodes array
          // These will be lazily resolved when called since they're defined later
          onSaveOutput: (n) => handleSaveNodeOutput(n),
          onDownloadOutput: (n) => handleDownloadNodeOutput(n)
        }
      }));

      setNodes(nodesWithHandlers);
      setEdges(flowData.edges);

      // Mark this workflow as loaded
      initialGraphLoadedRef.current = workflowId;

      // Fetch live status immediately so node colours reflect reality, not
      // the static graph snapshot (which has no status field on each node).
      refetchStatus().then((liveStatus) => {
        // If the workflow is still active, subscribe to SSE for live updates.
        if (liveStatus?.status === 'executing' || liveStatus?.status === 'paused') {
          startPolling();
        }
      });
    }
  }, [initialGraph, workflowId, setNodes, setEdges, handleNodeApprove, handleNodeReject, handleNodeRerun, handleNodeSelect, refetchStatus, startPolling]);

  // Cleanup notification timeouts on unmount or workflow change
  useEffect(() => {
    return () => {
      // Clear all pending notification timeouts
      notificationTimeoutsRef.current.forEach(timeoutId => clearTimeout(timeoutId));
      notificationTimeoutsRef.current = [];
    };
  }, [workflowId]);

  // Clear waitingNodesRef when workflow changes to prevent accumulation
  useEffect(() => {
    if (!status?.status || status.status === 'executing') {
      waitingNodesRef.current.clear();
    }
  }, [workflowId, status?.status]);

  // Clean up ReactFlow instance on unmount
  useEffect(() => {
    return () => {
      if (reactFlowInstance.current) {
        reactFlowInstance.current = null;
      }
    };
  }, []);

  // Update nodes when status changes (preserve handlers!)
  useEffect(() => {
    // Only update if status has nodes AND we currently have nodes to update
    if (status && status.nodes && status.nodes.length > 0) {
      console.log('📊 Status update received:', {
        workflowStatus: status.status,
        totalNodes: status.nodes.length,
        pendingApproval: status.pending_approval_nodes || 0,
        timestamp: new Date().toLocaleTimeString()
      });

      setNodes((nds) => {
        if (nds.length === 0) {
          console.warn('⚠️ Skipping status update - no nodes loaded yet');
          return nds;
        }
        const updated = nds.map((node) => {
          const statusNode = status.nodes.find((n) => n.id === node.id);
          if (statusNode) {
            // Check if data has actually changed before creating new objects
            if (!hasNodeDataChanged(node, statusNode)) {
              return node; // Return same reference if unchanged
            }

            // Detect if node just entered waiting_approval status
            const wasWaiting = node.data.status === 'waiting_approval';
            const nowWaiting = statusNode.status === 'waiting_approval';

            if (!wasWaiting && nowWaiting && !waitingNodesRef.current.has(node.id)) {
              // Node just started waiting for approval
              console.log(`🔔 APPROVAL REQUIRED: Node "${statusNode.display_name}" (${statusNode.node_key}) needs approval!`);
              waitingNodesRef.current.add(node.id);

              // Check if auto-approve is enabled and this is a simple approval node
              const updatedNode = {
                ...node,
                data: {
                  ...node.data,
                  status: statusNode.status,
                  displayName: statusNode.display_name || node.data.displayName,
                  approvalPrompt: statusNode.approval_prompt || node.data.approvalPrompt,
                  nodeKey: statusNode.node_key || node.data.nodeKey
                }
              };

              if (autoApprove) {
                // Auto-approve in next tick to avoid state conflicts
                // Capture these in the closure - they'll be defined by the time setTimeout fires
                const nodeKey = statusNode.node_key;
                const displayName = statusNode.display_name;
                setTimeout(async () => {
                  console.log(`⚡ AUTO-APPROVE: Automatically approving simple node "${displayName}"`);
                  try {
                    await axios.post(
                      `/api/v1/workflows/${workflowId}/nodes/${nodeKey}/approve`,
                      {
                        approved: true,
                        decision_notes: 'Auto-approved (simple confirmation)',
                      }
                    );
                    // Call status refetch after approval
                    // These functions are available via closure by the time setTimeout fires
                    refetchStatus();
                    setTimeout(() => {
                      resume();
                      startPolling();
                    }, 500);
                  } catch (error) {
                    console.error('Auto-approve failed:', error);
                  }
                }, 100);
                return updatedNode; // Don't show notification for auto-approved nodes
              }

              // Show notification for manual approval
              const notification = {
                id: `approval-${node.id}-${Date.now()}`,
                nodeId: node.id,
                nodeName: statusNode.display_name || node.data.displayName,
                message: `Node "${statusNode.display_name || node.data.displayName}" requires your approval`,
                timestamp: Date.now()
              };

              setApprovalNotifications(prev => [...prev, notification]);

              // Auto-dismiss after 10 seconds
              const timeoutId = setTimeout(() => {
                setApprovalNotifications(prev => prev.filter(n => n.id !== notification.id));
              }, 10000);

              // Track timeout for cleanup
              notificationTimeoutsRef.current.push(timeoutId);
            } else if (wasWaiting && !nowWaiting) {
              // Node no longer waiting - remove from tracking
              waitingNodesRef.current.delete(node.id);
              console.log(`✓ Node "${statusNode.display_name}" (${statusNode.node_key}) approved and continuing (now ${statusNode.status})`);
            }

            return {
              ...node,
              data: {
                ...node.data,
                // Update status fields
                status: statusNode.status,
                cost: statusNode.cost,
                tokensInput: statusNode.tokens_input,
                tokensOutput: statusNode.tokens_output,
                humanOutput: statusNode.human_output,
                error: statusNode.error,
                // Preserve or update editable fields
                displayName: statusNode.display_name || node.data.displayName,
                description: statusNode.description || node.data.description,
                prompt_template: statusNode.prompt_template || node.data.prompt_template,
                system_prompt: statusNode.system_prompt || node.data.system_prompt,
                selected_model: statusNode.selected_model || node.data.selected_model,
                complexity: statusNode.complexity_score || node.data.complexity,
                requiresApproval: statusNode.requires_approval !== undefined ? statusNode.requires_approval : node.data.requiresApproval,
                approvalPrompt: statusNode.approval_prompt || node.data.approvalPrompt,
                estimatedTokens: statusNode.estimated_tokens || node.data.estimatedTokens,
                // CRITICAL: Preserve handlers from existing data
                // Handlers are set during initial load and preserved through updates
                onApprove: node.data.onApprove,
                onReject: node.data.onReject,
                onRerun: node.data.onRerun,
                onSelect: node.data.onSelect,
                onSaveOutput: node.data.onSaveOutput,
                onDownloadOutput: node.data.onDownloadOutput
              }
            };
          }
          return node;
        });
        // console.log('Nodes after status update:', updated.length);
        return updated;
      });
    }
    // Note: refetchStatus, resume, startPolling are captured via closure in setTimeout
    // They don't need to be in deps because the effect only re-runs on status/autoApprove changes
  }, [status, setNodes, autoApprove, isSimpleApprovalNode, workflowId]);

  // Handle execution.
  //
  // brainstorm-z8t2.24: inputValues is supplied when the user answers the
  // grounding gate's form. A null result means either a real error (rendered as
  // the red banner) or a 422 asking for inputs (rendered as ExecutionInputForm
  // from `requiredInputs`) — the hook distinguishes them, so there is one path
  // for the first attempt and the resubmission.
  const handleExecute = useCallback(async (maybeInputValues = null) => {
    // This is wired to onClick (line ~378), so the first argument is a React
    // SyntheticEvent on a button press and a plain values object only when
    // called from ExecutionInputForm. Without this guard the event would be
    // serialised into the request body.
    const inputValues =
      maybeInputValues && typeof maybeInputValues === 'object' && !maybeInputValues.nativeEvent
        ? maybeInputValues
        : null;
    const result = await execute(executionMode, inputValues);
    if (result) {
      // Optimistically set status to 'executing' so the UI switches immediately
      // SSE will confirm and update further once it arrives (~60s later)
      setStatus(prev => prev ? { ...prev, status: 'executing' } : { status: 'executing' });
      startPolling();
      refetchCost();
    }
  }, [execute, executionMode, setStatus, startPolling, refetchCost]);

  const handleResume = useCallback(async () => {
    const result = await resume();
    if (result) {
      startPolling();
      refetchCost();
    }
  }, [resume, startPolling, refetchCost]);

  const handlePause = useCallback(async () => {
    await pause();
    stopPolling();
  }, [pause, stopPolling]);

  const handleInterrupt = useCallback(
    async (instructions) => {
      await interrupt(instructions);
      refetchStatus();
      refetchCost();
    },
    [interrupt, refetchStatus, refetchCost]
  );

  const handleRetryDecomposition = useCallback(async (maxDepth) => {
    const taskBriefId = status?.task_brief_id;
    if (!taskBriefId) return;

    setRetryingDecomposition(true);
    try {
      await axios.post(`/api/v1/task-briefs/${taskBriefId}/approve`, {
        proceed_to_decomposition: true,
        max_depth_override: maxDepth
      });
      // Optimistically show processing state and start polling
      setStatus(prev => prev ? { ...prev, status: 'processing', error_message: null } : { status: 'processing' });
      startPolling();
    } catch (err) {
      console.error('Retry decomposition failed:', err);
    } finally {
      setRetryingDecomposition(false);
    }
  }, [status?.task_brief_id, setStatus, startPolling]);

  const handleNodeSave = useCallback(
    async (nodeId, updates) => {
      await updateNode(nodeId, updates);

      // Update the nodes state with the new data
      setNodes((currentNodes) => {
        return currentNodes.map((node) => {
          if (node.id === nodeId) {
            // Update the node's data with the saved changes
            return {
              ...node,
              data: {
                ...node.data,
                displayName: updates.display_name || node.data.displayName,
                description: updates.description || node.data.description,
                prompt_template: updates.prompt_template || node.data.prompt_template,
                system_prompt: updates.system_prompt || node.data.system_prompt,
                selected_model: updates.selected_model || node.data.selected_model,
                complexity: updates.complexity_score || node.data.complexity,
                estimatedTokens: updates.estimated_tokens || node.data.estimatedTokens,
                requiresApproval: updates.requires_approval !== undefined ? updates.requires_approval : node.data.requiresApproval,
                approvalPrompt: updates.approval_prompt || node.data.approvalPrompt
              }
            };
          }
          return node;
        });
      });

      // Also update editingNode if it's the same node (so reopening modal shows fresh data)
      setEditingNode((currentEditingNode) => {
        if (currentEditingNode && currentEditingNode.id === nodeId) {
          return {
            ...currentEditingNode,
            data: {
              ...currentEditingNode.data,
              displayName: updates.display_name || currentEditingNode.data.displayName,
              description: updates.description || currentEditingNode.data.description,
              prompt_template: updates.prompt_template || currentEditingNode.data.prompt_template,
              system_prompt: updates.system_prompt || currentEditingNode.data.system_prompt,
              selected_model: updates.selected_model || currentEditingNode.data.selected_model,
              complexity: updates.complexity_score || currentEditingNode.data.complexity,
              estimatedTokens: updates.estimated_tokens || currentEditingNode.data.estimatedTokens,
              requiresApproval: updates.requires_approval !== undefined ? updates.requires_approval : currentEditingNode.data.requiresApproval,
              approvalPrompt: updates.approval_prompt || currentEditingNode.data.approvalPrompt
            }
          };
        }
        return currentEditingNode;
      });

      // Refresh status to get updated node counts
      refetchStatus();
    },
    [updateNode, refetchStatus, setNodes]
  );

  const handleNodeApproval = useCallback(
    async (nodeId, approvalData) => {
      // Get the node key from the approving node
      const nodeKey = approvingNode?.data?.nodeKey;

      if (!nodeKey) {
        console.error('No node key found for approval');
        return;
      }

      try {
        // Call the approval API directly with axios
        const response = await axios.post(
          `/api/v1/workflows/${workflowId}/nodes/${nodeKey}/approve`,
          approvalData
        );

        console.log('✓ Approval submitted:', response.data);

        // Refetch workflow status to update node states
        await refetchStatus();

        // CRITICAL FIX: Resume workflow execution after approval
        // The workflow pauses when waiting for approval, so we must explicitly resume it
        console.log('Resuming workflow execution after approval...');
        await resume();

        // Start polling to track execution progress
        startPolling();

        // Refresh cost data
        refetchCost();

        return response.data;
      } catch (error) {
        console.error('Failed to approve node:', error);
        throw error;
      }
    },
    [approvingNode, workflowId, refetchStatus, resume, startPolling, refetchCost]
  );

  // Context menu handlers
  const onNodeContextMenu = useCallback((event, node) => {
    event.preventDefault();
    setContextSelectedNode(node);
    setContextMenu({
      x: event.clientX,
      y: event.clientY,
      nodeType: 'workflowNode',
      nodeData: node.data || {}
    });
  }, []);

  const onPaneContextMenu = useCallback((event) => {
    event.preventDefault();
    setContextSelectedNode(null);
    setContextMenu({
      x: event.clientX,
      y: event.clientY,
      nodeType: 'pane',
      nodeData: {}
    });
  }, []);

  const onPaneClick = useCallback(() => {
    setContextMenu(null);
  }, []);

  // File attachment handlers
  const handleUploadAttachFile = useCallback(() => {
    setContextMenu(null);
    const input = document.createElement('input');
    input.type = 'file';
    input.onchange = async (event) => {
      const file = event.target.files[0];
      if (file && contextSelectedNode) {
        try {
          console.log('Uploading file:', file.name);
          const uploadResult = await smartUpload(file, (percent) => {
            console.log(`Upload progress: ${percent}%`);
          });

          console.log('File uploaded:', uploadResult);

          // Attach to node
          setNodes((nds) =>
            nds.map((n) =>
              n.id === contextSelectedNode.id
                ? {
                  ...n,
                  data: {
                    ...n.data,
                    attachedFiles: [
                      ...(n.data.attachedFiles || []),
                      {
                        file_id: uploadResult.file_id,
                        filename: uploadResult.filename,
                        file_size: uploadResult.file_size,
                        mime_type: uploadResult.mime_type
                      }
                    ]
                  }
                }
                : n
            )
          );
        } catch (error) {
          console.error('File upload failed:', error);

          // Check if it's an authentication error
          if (error.message && error.message.includes('Authentication required')) {
            showToast('Please log in to upload files. Your session may have expired.', 'error', 8000);
          } else {
            showToast('File upload failed: ' + error.message, 'error', 8000);
          }
        }
      }
      // Clean up to prevent memory leaks
      input.onchange = null;
      input.remove();
    };
    input.click();
  }, [contextSelectedNode, setNodes]);

  const handleSelectFromLibrary = useCallback(() => {
    setContextMenu(null);
    setShowFilePicker(true);
  }, []);

  const handleFileSelected = useCallback((selectedFiles) => {
    if (!contextSelectedNode) return;

    const files = Array.isArray(selectedFiles) ? selectedFiles : [selectedFiles];

    setNodes((nds) =>
      nds.map((n) =>
        n.id === contextSelectedNode.id
          ? {
            ...n,
            data: {
              ...n.data,
              attachedFiles: [
                ...(n.data.attachedFiles || []),
                ...files.map(f => ({
                  file_id: f.file_id,
                  filename: f.filename,
                  file_size: f.file_size,
                  mime_type: f.mime_type
                }))
              ]
            }
          }
          : n
      )
    );
  }, [contextSelectedNode, setNodes]);

  // Detect output format and return appropriate file extension and MIME type
  // Supports: JSON, XML, SVG, HTML, YAML, Markdown, CSV, TSV, LaTeX, SQL, Python, JavaScript, Shell,
  //           Dockerfile, INI, Env, Logs, PDF, Office docs, archives, and images (base64)
  const detectOutputFormat = (content, nodeType) => {
    if (typeof content === 'string') {
      const trimmed = content.trim();

      // === BASE64 DATA DETECTION (check first for binary formats) ===
      if (trimmed.startsWith('data:')) {
        // Base64 images
        const imageMatch = trimmed.match(/^data:image\/(\w+);base64,/);
        if (imageMatch) {
          return { extension: imageMatch[1], mimeType: `image/${imageMatch[1]}`, isBase64: true };
        }

        // Base64 PDF
        if (trimmed.startsWith('data:application/pdf;base64,')) {
          return { extension: 'pdf', mimeType: 'application/pdf', isBase64: true };
        }

        // Base64 ZIP/Archives
        if (trimmed.match(/^data:application\/(zip|x-zip-compressed|x-tar|x-7z-compressed);base64,/)) {
          const ext = trimmed.includes('tar') ? 'tar.gz' : trimmed.includes('7z') ? '7z' : 'zip';
          return { extension: ext, mimeType: 'application/zip', isBase64: true };
        }

        // Base64 Excel
        if (trimmed.includes('spreadsheetml.sheet')) {
          return { extension: 'xlsx', mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', isBase64: true };
        }

        // Base64 Word
        if (trimmed.includes('wordprocessingml.document')) {
          return { extension: 'docx', mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', isBase64: true };
        }

        // Base64 PowerPoint
        if (trimmed.includes('presentationml.presentation')) {
          return { extension: 'pptx', mimeType: 'application/vnd.openxmlformats-officedocument.presentationml.presentation', isBase64: true };
        }
      }

      // === TEXT FORMAT DETECTION ===

      // JSON detection
      if ((trimmed.startsWith('{') && trimmed.endsWith('}')) ||
          (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
        try {
          JSON.parse(trimmed);
          return { extension: 'json', mimeType: 'application/json' };
        } catch (e) {
          // Not valid JSON, continue
        }
      }

      // XML detection (check before HTML)
      if (trimmed.startsWith('<?xml') ||
          (trimmed.startsWith('<') && trimmed.includes('xmlns') && !trimmed.includes('<!DOCTYPE html'))) {
        return { extension: 'xml', mimeType: 'application/xml' };
      }

      // SVG detection (XML-based vector graphics)
      if (trimmed.match(/<svg[\s>]/) || trimmed.startsWith('<svg')) {
        return { extension: 'svg', mimeType: 'image/svg+xml' };
      }

      // HTML detection
      if (trimmed.match(/<!DOCTYPE html/i) || trimmed.match(/<html[\s>]/i) ||
          (trimmed.startsWith('<') && (trimmed.includes('<head') || trimmed.includes('<body')))) {
        return { extension: 'html', mimeType: 'text/html' };
      }

      // YAML detection
      if (trimmed.match(/^---\n/) ||
          (trimmed.match(/^\w+:\s+/m) && trimmed.split('\n').filter(l => l.match(/^\s*\w+:\s+/)).length > 2)) {
        return { extension: 'yaml', mimeType: 'application/x-yaml' };
      }

      // Markdown detection (check after YAML since they can overlap)
      if (trimmed.match(/^#{1,6}\s/) || trimmed.includes('```') ||
          trimmed.match(/\[.*?\]\(.*?\)/) || trimmed.match(/^[-*+]\s/m)) {
        return { extension: 'md', mimeType: 'text/markdown' };
      }

      // CSV detection
      if (trimmed.includes(',') && trimmed.split('\n').length > 1) {
        const lines = trimmed.split('\n').filter(l => l.trim());
        if (lines.length > 1 && lines.filter(l => l.split(',').length > 1).length / lines.length > 0.8) {
          return { extension: 'csv', mimeType: 'text/csv' };
        }
      }

      // TSV detection (tab-separated values)
      if (trimmed.includes('\t') && trimmed.split('\n').length > 1) {
        const lines = trimmed.split('\n').filter(l => l.trim());
        if (lines.length > 1 && lines.filter(l => l.split('\t').length > 1).length / lines.length > 0.8) {
          return { extension: 'tsv', mimeType: 'text/tab-separated-values' };
        }
      }

      // LaTeX detection
      if (trimmed.match(/\\documentclass|\\begin{document}|\\section|\\usepackage/)) {
        return { extension: 'tex', mimeType: 'application/x-latex' };
      }

      // SQL detection
      if (trimmed.match(/^\s*(SELECT|INSERT|UPDATE|DELETE|CREATE|ALTER|DROP|GRANT)\s/im)) {
        return { extension: 'sql', mimeType: 'application/sql' };
      }

      // Python detection
      if (trimmed.match(/^(def |class |import |from .* import|#!.*python)/m) ||
          (trimmed.includes('def ') && (trimmed.includes('import ') || trimmed.includes('return ')))) {
        return { extension: 'py', mimeType: 'text/x-python' };
      }

      // JavaScript/TypeScript detection
      if (trimmed.match(/^(function |const |let |var |import |export |interface |type |\/\/|\/\*)/m) ||
          (trimmed.includes('=>') && (trimmed.includes('function') || trimmed.includes('const')))) {
        const ext = trimmed.includes('interface ') || trimmed.includes(': ') ? 'ts' : 'js';
        return { extension: ext, mimeType: ext === 'ts' ? 'text/typescript' : 'text/javascript' };
      }

      // Java detection
      if (trimmed.match(/^(public |private |protected |class |interface |package |import java)/m)) {
        return { extension: 'java', mimeType: 'text/x-java' };
      }

      // C/C++ detection
      if (trimmed.match(/^(#include |int main\(|void |return 0;|#define)/m)) {
        const ext = trimmed.includes('std::') || trimmed.includes('cout') ? 'cpp' : 'c';
        return { extension: ext, mimeType: `text/x-${ext}src` };
      }

      // Shell script detection
      if (trimmed.match(/^#!.*(bash|sh)|^(export |source |echo |cd |ls )/m)) {
        return { extension: 'sh', mimeType: 'application/x-sh' };
      }

      // Dockerfile detection
      if (trimmed.match(/^FROM\s+|^RUN\s+|^COPY\s+|^WORKDIR\s+|^EXPOSE\s+/m)) {
        return { extension: 'dockerfile', mimeType: 'text/plain', note: 'Dockerfile' };
      }

      // INI/Config file detection
      if (trimmed.match(/^\[.*\]$/m) && trimmed.match(/^\w+=.+$/m)) {
        return { extension: 'ini', mimeType: 'text/plain', note: 'Config file' };
      }

      // Environment file detection
      if (trimmed.match(/^\w+=[^\n]+$/m) && trimmed.split('\n').filter(l => l.match(/^\w+=/)).length > 2) {
        return { extension: 'env', mimeType: 'text/plain', note: 'Environment variables' };
      }

      // Log file detection
      if (trimmed.match(/^\d{4}-\d{2}-\d{2}|^\[\d{4}-\d{2}-\d{2}|ERROR|WARNING|INFO|DEBUG/m)) {
        return { extension: 'log', mimeType: 'text/plain', note: 'Log file' };
      }

      // URL to file (treat as text reference)
      if (trimmed.match(/^https?:\/\/.*\.(png|jpg|jpeg|gif|webp|svg|pdf|zip|xlsx|docx)$/i)) {
        return { extension: 'txt', mimeType: 'text/plain', note: 'File URL reference' };
      }
    }

    // Check node type for hints when content analysis fails
    if (nodeType) {
      const nodeTypeLower = nodeType.toLowerCase();
      if (nodeTypeLower.includes('image')) {
        return { extension: 'txt', mimeType: 'text/plain', note: 'Image node (text output only)' };
      }
      if (nodeTypeLower.includes('pdf')) {
        return { extension: 'txt', mimeType: 'text/plain', note: 'PDF node (text output only)' };
      }
      if (nodeTypeLower.includes('code')) {
        return { extension: 'txt', mimeType: 'text/plain', note: 'Code output' };
      }
    }

    // Default to plain text
    return { extension: 'txt', mimeType: 'text/plain' };
  };

  // Save node output as file
  const handleSaveNodeOutput = useCallback(async (node) => {
    if (!node?.data?.humanOutput) {
      showToast('No output to save', 'warning');
      return;
    }

    try {
      const content = node.data.humanOutput;
      const nodeType = node.data.nodeType;
      const displayName = node.data.displayName;

      // Detect output format
      const format = detectOutputFormat(content, nodeType);

      // Generate intelligent filename
      const baseName = generateIntelligentFilename(content, nodeType, displayName);
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `${workflowIdentifier}_${baseName}_${timestamp}.${format.extension}`;

      // Create blob and upload
      let blob, file;

      if (format.isBase64) {
        // Handle base64 image data
        const base64Data = content.split(',')[1];
        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        blob = new Blob([bytes], { type: format.mimeType });
        file = new File([blob], filename, { type: format.mimeType });
      } else {
        // Handle text-based content
        blob = new Blob([content], { type: format.mimeType });
        file = new File([blob], filename, { type: format.mimeType });
      }

      console.log('Saving node output:', filename, `(${format.mimeType})`);

      const uploadResult = await smartUpload(file, (percent) => {
        console.log(`Upload progress: ${percent}%`);
      }, 100 * 1024 * 1024, { workflowId, isPublic: false });

      console.log('✓ Output saved:', uploadResult);

      // Show success toast with filename
      const formatNote = format.note ? ` (${format.note})` : '';
      showToast(`Output saved as: ${filename}${formatNote}`, 'success');

      return uploadResult;
    } catch (error) {
      console.error('Failed to save output:', error);

      if (error.message && error.message.includes('Authentication required')) {
        showToast('Please log in to save workflow output. Your session may have expired.', 'error', 8000);
      } else {
        showToast('Failed to save output: ' + error.message, 'error', 8000);
      }
    }
  }, [workflowIdentifier, showToast]);

  // Download node output directly
  const handleDownloadNodeOutput = useCallback((node) => {
    if (!node?.data?.humanOutput) {
      showToast('No output to download', 'warning');
      return;
    }

    const content = node.data.humanOutput;
    const nodeType = node.data.nodeType;
    const displayName = node.data.displayName;

    // Detect output format
    const format = detectOutputFormat(content, nodeType);

    // Generate intelligent filename
    const baseName = generateIntelligentFilename(content, nodeType, displayName);
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `${workflowIdentifier}_${baseName}_${timestamp}.${format.extension}`;

    // Create download
    let blob;

    if (format.isBase64) {
      // Handle base64 image data
      const base64Data = content.split(',')[1];
      const binaryString = atob(base64Data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      blob = new Blob([bytes], { type: format.mimeType });
    } else {
      // Handle text-based content
      blob = new Blob([content], { type: format.mimeType });
    }

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    console.log('✓ Downloaded:', filename);
  }, [workflowIdentifier]);

  // Download the persisted final output file from fileserver
  const handleDownloadFinalOutputFile = useCallback(async (fileId, filename) => {
    try {
      await downloadFile(fileId, filename || 'workflow_output.txt');
    } catch (err) {
      console.error('Failed to download final output from fileserver:', err);
      showToast('Download failed: ' + err.message, 'error', 8000);
    }
  }, []);

  // Load workflow files from fileserver
  const handleLoadWorkflowFiles = useCallback(async () => {
    if (!initialWorkflow?.id) return;
    setWorkflowFilesLoading(true);
    try {
      const data = await listWorkflowFiles(initialWorkflow.id);
      setWorkflowFiles(data.files || []);
    } catch (err) {
      console.error('Failed to load workflow files:', err);
    } finally {
      setWorkflowFilesLoading(false);
    }
  }, [initialWorkflow?.id]);

  // Toggle workflow files panel and load on open
  const handleToggleWorkflowFiles = useCallback(() => {
    const next = !showWorkflowFiles;
    setShowWorkflowFiles(next);
    if (next) handleLoadWorkflowFiles();
  }, [showWorkflowFiles, handleLoadWorkflowFiles]);

  // Get final output node (last completed node with output)
  const finalOutputNode = useMemo(() => {
    const completedNodes = nodes.filter(n =>
      n.data.status === 'completed' && n.data.humanOutput
    );

    if (completedNodes.length === 0) return null;

    // Find node with highest depth level (deepest in tree)
    return completedNodes.reduce((deepest, node) => {
      const nodeDepth = node.data.depthLevel || 0;
      const deepestDepth = deepest?.data?.depthLevel || 0;
      return nodeDepth > deepestDepth ? node : deepest;
    }, completedNodes[0]);
  }, [nodes]);

  // Refresh cost data (node completion counts) when workflow finishes
  useEffect(() => {
    if (status?.status === 'completed' || status?.status === 'failed') {
      refetchCost();
    }
  }, [status?.status, refetchCost]);

  // Auto-save final output when workflow completes
  useEffect(() => {
    if (status?.status === 'completed' && finalOutputNode && !finalOutputNode.data.outputSaved) {
      console.log('Workflow completed - auto-saving final output');
      handleSaveNodeOutput(finalOutputNode).then(() => {
        // Mark as saved
        setNodes(nds => nds.map(n =>
          n.id === finalOutputNode.id
            ? { ...n, data: { ...n.data, outputSaved: true } }
            : n
        ));
      });
    }
  }, [status, finalOutputNode, handleSaveNodeOutput, setNodes]);

  return (
    <div className={`flex flex-col bg-gray-50 ${isFullscreen ? 'fixed inset-0 z-50 h-full' : 'min-h-screen'}`}>
      {/* Top controls */}
      <div className="p-4 space-y-4">
        {/* Workflow Header with Identifier */}
        <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Tag size={20} className="text-blue-600" />
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wide">Workflow ID</div>
              <div className="text-sm font-mono font-semibold text-gray-900">
                {workflowIdentifier}
              </div>
            </div>
          </div>

          {/* Final Output Download */}
          {(status?.output_file_id || finalOutputNode) && (
            <div className="flex items-center gap-2">
              <div className="text-xs text-gray-600 mr-2">
                Final Output Available
              </div>
              {/* Prefer fileserver-persisted file download */}
              {status?.output_file_id ? (
                <button
                  onClick={() => handleDownloadFinalOutputFile(
                    status.output_file_id,
                    initialWorkflow?.output_filename || `workflow_${String(initialWorkflow?.id || '').slice(0, 8)}_output.txt`
                  )}
                  className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-sm"
                  title="Download final output file from fileserver"
                >
                  <Download size={16} />
                  <span className="text-sm font-medium">Download Output</span>
                </button>
              ) : finalOutputNode ? (
                <button
                  onClick={() => handleDownloadNodeOutput(finalOutputNode)}
                  className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-sm"
                  title="Download final workflow output"
                >
                  <Download size={16} />
                  <span className="text-sm font-medium">Download Output</span>
                </button>
              ) : null}
              {finalOutputNode && (
                <button
                  onClick={() => handleSaveNodeOutput(finalOutputNode)}
                  className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                  title="Save to file library"
                >
                  <Save size={16} />
                  <span className="text-sm font-medium">Save to Library</span>
                </button>
              )}
              <button
                onClick={() => {
                  // Generate intelligent name + description from available context
                  const task = initialWorkflow?.task_description || '';
                  const domain = initialWorkflow?.domain || '';
                  const company = initialWorkflow?.company || '';
                  const nodeNames = nodes
                    .filter(n => n.data?.displayName)
                    .map(n => n.data.displayName)
                    .slice(0, 5);

                  // Name: title-case the task description, trimmed to ~60 chars
                  const rawName = task
                    ? task.charAt(0).toUpperCase() + task.slice(1).replace(/[.!?]+$/, '')
                    : 'Workflow Template';
                  const suggestedName = rawName.length > 60 ? rawName.slice(0, 57) + '…' : rawName;

                  // Description: combine what we know
                  const parts = [];
                  if (domain) parts.push(`Domain: ${domain}`);
                  if (company) parts.push(`Company: ${company}`);
                  if (nodeNames.length > 0) parts.push(`Steps: ${nodeNames.join(' → ')}`);
                  const suggestedDesc = parts.join('. ');

                  setSaveTemplateName(suggestedName);
                  setSaveTemplateDescription(suggestedDesc);
                  setSaveTemplateError(null);
                  setShowSaveAsTemplateModal(true);
                }}
                className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors shadow-sm"
                title="Save workflow as reusable template"
              >
                <Bookmark size={16} />
                <span className="text-sm font-medium">Save as Template</span>
              </button>
            </div>
          )}
        </div>

        {/* Toolbar with all action buttons */}
        <div className="flex justify-between items-center gap-4">
          {/* Left side: workflow actions */}
          <div className="flex items-center gap-2">
            {/* Files viewer toggle */}
            <button
              onClick={() => setShowFilesViewer(!showFilesViewer)}
              className={`flex items-center gap-2 px-3 py-2 border rounded-lg hover:bg-gray-50 transition-colors shadow-sm ${
                showFilesViewer ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-gray-300'
              }`}
              title="View attached files"
            >
              <Paperclip size={18} />
              <span className="text-sm font-medium">Files</span>
            </button>

            {/* Workflow generated files toggle */}
            {(status?.intermediate_file_ids?.length > 0 || status?.output_file_id) && (
              <button
                onClick={handleToggleWorkflowFiles}
                className={`flex items-center gap-2 px-3 py-2 border rounded-lg hover:bg-gray-50 transition-colors shadow-sm ${
                  showWorkflowFiles ? 'bg-purple-50 border-purple-300 text-purple-700' : 'bg-white border-gray-300'
                }`}
                title="View files generated by this workflow"
              >
                <Download size={18} />
                <span className="text-sm font-medium">
                  Generated Files
                  {status?.intermediate_file_ids?.length > 0 && (
                    <span className="ml-1 bg-purple-100 text-purple-700 rounded-full px-1.5 py-0.5 text-xs">
                      {status.intermediate_file_ids.length}
                    </span>
                  )}
                </span>
              </button>
            )}

            {/* Auto-approve toggle */}
            <button
              onClick={() => setAutoApprove(!autoApprove)}
              className={`flex items-center gap-2 px-3 py-2 border rounded-lg hover:bg-gray-50 transition-colors shadow-sm ${
                autoApprove ? 'bg-green-50 border-green-300 text-green-700' : 'bg-white border-gray-300'
              }`}
              title={autoApprove ? 'Auto-approve enabled' : 'Auto-approve disabled'}
            >
              <Zap size={18} />
              <span className="text-sm font-medium">Auto-Approve</span>
              {autoApprove && <span className="text-xs bg-green-200 px-1.5 py-0.5 rounded">ON</span>}
            </button>

            {/* Background workflow button */}
            <button
              onClick={() => {
                console.log('Backgrounding workflow...');
                // Navigate to workflow list or home
                if (window.location.pathname.includes('/workflow/')) {
                  window.location.href = '/app';
                }
              }}
              className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
              title="Background this workflow"
            >
              <MinusCircle size={18} />
              <span className="text-sm font-medium">Background</span>
            </button>

            {/* Cancel workflow button */}
            <button
              onClick={async () => {
                if (window.confirm('Are you sure you want to cancel this workflow? This cannot be undone.')) {
                  try {
                    await axios.post(`/api/v1/workflows/${workflowId}/cancel`);
                    console.log('Workflow cancelled');
                    stopPolling();
                    refetchStatus();
                  } catch (error) {
                    console.error('Failed to cancel workflow:', error);
                    showToast('Failed to cancel workflow: ' + (error.response?.data?.detail || error.message), 'error', 8000);
                  }
                }
              }}
              className="flex items-center gap-2 px-3 py-2 bg-white border border-red-300 rounded-lg hover:bg-red-50 text-red-600 transition-colors shadow-sm"
              title="Cancel workflow"
            >
              <Square size={18} />
              <span className="text-sm font-medium">Cancel</span>
            </button>

            {/* Discard workflow button — only for draft/ready (pre-execution) workflows */}
            {(!status?.status || status?.status === 'draft' || status?.status === 'ready' || status?.status === 'pending') && (
              <button
                onClick={async () => {
                  if (window.confirm('Discard this workflow? This cannot be undone.')) {
                    try {
                      await axios.post(`/api/v1/workflows/${workflowId}/cancel`);
                      console.log('Workflow discarded');
                      stopPolling();
                      // Navigate back to app
                      if (window.location.pathname.includes('/workflow/')) {
                        window.location.href = '/app';
                      }
                    } catch (error) {
                      console.error('Failed to discard workflow:', error);
                      showToast('Failed to discard workflow: ' + (error.response?.data?.detail || error.message), 'error', 8000);
                    }
                  }
                }}
                className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white border border-red-700 rounded-lg hover:bg-red-700 transition-colors shadow-sm"
                title="Discard this workflow"
              >
                <Trash2 size={18} />
                <span className="text-sm font-medium">Discard</span>
              </button>
            )}
          </div>

          {/* Right side: fullscreen */}
          <button
            onClick={() => setIsFullscreen(!isFullscreen)}
            className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
            title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
          >
            {isFullscreen ? (
              <>
                <Minimize2 size={18} />
                <span className="text-sm font-medium">Exit Fullscreen</span>
              </>
            ) : (
              <>
                <Maximize2 size={18} />
                <span className="text-sm font-medium">Fullscreen</span>
              </>
            )}
          </button>
        </div>

        <ExecutionModeSelector value={executionMode} onChange={setExecutionMode} />

        {/* Progress Summary */}
        <WorkflowProgressSummary workflow={initialWorkflow} nodes={nodes} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <ControlPanel
              workflow={initialWorkflow}
              status={status}
              executionMode={executionMode}
              onExecutionModeChange={setExecutionMode}
              onExecute={handleExecute}
              onResume={handleResume}
              onPause={handlePause}
              onInterrupt={handleInterrupt}
              executing={executing}
              paused={paused}
              onStatusToggle={handleStatusToggle}
              transitioning={transitioning}
              onRetryDecomposition={handleRetryDecomposition}
              retryingDecomposition={retryingDecomposition}
            />
          </div>
          <div>
            <NodeTypeSummaryPanel costData={costData} loading={costLoading} nodes={nodes} />
          </div>
        </div>

        {/* brainstorm-z8t2.24: the grounding gate is asking for data only the
            user has. Rendered above the error banner because it is not a
            failure — and the hook keeps the two states mutually exclusive. */}
        {requiredInputs && (
          <ExecutionInputForm
            requiredInputs={requiredInputs}
            submitting={executing}
            onSubmit={handleExecute}
            onCancel={clearRequiredInputs}
          />
        )}

        {executionError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2 text-red-700">
            <XCircle size={20} />
            <span>{typeof executionError === 'string' ? executionError : executionError?.message || executionError?.detail || JSON.stringify(executionError)}</span>
          </div>
        )}
      </div>

      {/* Real-time event log (dev panel) */}
      <WorkflowEventLog workflowId={workflowId} />

      {/* React Flow graph */}
      <div
        ref={reactFlowWrapper}
        className={`bg-white rounded-lg shadow m-4 ${isFullscreen ? 'flex-1' : ''}`}
        style={{ height: isFullscreen ? undefined : 'clamp(600px, 70vh, 2000px)' }}
      >
        {nodes.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-gray-500">
              <p className="text-lg mb-2">No graph nodes to display</p>
              <p className="text-sm">Workflow ID: {workflowId}</p>
            </div>
          </div>
        ) : (
          <ReactFlow
            key={`reactflow-${workflowId}`}
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeContextMenu={onNodeContextMenu}
            onPaneContextMenu={onPaneContextMenu}
            onPaneClick={onPaneClick}
            onInit={(instance) => { reactFlowInstance.current = instance; }}
            nodeTypes={aiWorkflowNodeTypes}
            nodesSelectable={false}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            minZoom={0.1}
            maxZoom={1.5}
          >
            <Background />
            <Controls />
            <MiniMap
              nodeColor={(node) => {
                const colors = {
                  data_retrieval: '#3B82F6',
                  llm_analysis: '#8B5CF6',
                  reconciliation: '#F97316',
                  human_approval: '#EAB308',
                  decomposition: '#14B8A6'
                };
                return colors[node.type] || '#6B7280';
              }}
            />
          </ReactFlow>
        )}
      </div>

      {/* Node Edit Modal */}
      <AIWorkflowNodeEditModal
        node={editingNode}
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingNode(null);
        }}
        onSave={handleNodeSave}
      />

      {/* Node Approval Modal */}
      <AIWorkflowNodeApprovalModal
        node={approvingNode}
        isOpen={isApprovalModalOpen}
        onClose={() => {
          setIsApprovalModalOpen(false);
          setApprovingNode(null);
        }}
        onApprove={handleNodeApproval}
        workflowId={workflowId}
      />

      {/* Save as Template Modal */}
      {showSaveAsTemplateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center gap-3 mb-4">
              <Bookmark size={20} className="text-purple-600" />
              <h2 className="text-lg font-semibold text-gray-900">Save as Template</h2>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Save this completed workflow as a reusable template so you can start new workflows from it.
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Template Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={saveTemplateName}
                  onChange={e => setSaveTemplateName(e.target.value)}
                  placeholder="e.g. Market Research Pipeline"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                  value={saveTemplateDescription}
                  onChange={e => setSaveTemplateDescription(e.target.value)}
                  placeholder="Briefly describe what this template does…"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                />
              </div>
              {saveTemplateError && (
                <p className="text-sm text-red-600">{saveTemplateError}</p>
              )}
            </div>
            <div className="flex gap-3 mt-5 justify-end">
              <button
                onClick={() => setShowSaveAsTemplateModal(false)}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={saveTemplateLoading}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!saveTemplateName.trim()) {
                    setSaveTemplateError('Please enter a template name.');
                    return;
                  }
                  setSaveTemplateLoading(true);
                  setSaveTemplateError(null);
                  try {
                    await saveWorkflowAsTemplate(
                      workflowId,
                      saveTemplateName.trim(),
                      saveTemplateDescription.trim() || null
                    );
                    setShowSaveAsTemplateModal(false);
                  } catch (err) {
                    const detail = err?.response?.data?.detail;
                    setSaveTemplateError(
                      typeof detail === 'string' ? detail
                      : Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join('; ')
                      : detail ? JSON.stringify(detail)
                      : 'Failed to save template. Please try again.'
                    );
                  } finally {
                    setSaveTemplateLoading(false);
                  }
                }}
                disabled={saveTemplateLoading || !saveTemplateName.trim()}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saveTemplateLoading ? (
                  <>
                    <RefreshCw size={14} className="animate-spin" />
                    Saving…
                  </>
                ) : (
                  <>
                    <Bookmark size={14} />
                    Save Template
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approval Notifications (Toast) */}
      <div className="fixed bottom-4 right-4 z-50 space-y-2 max-w-md">
        {approvalNotifications.map((notification) => (
          <div
            key={notification.id}
            className="bg-orange-500 text-white rounded-lg shadow-2xl p-4 flex items-start gap-3 animate-slide-in-right border-2 border-orange-600 cursor-pointer hover:bg-orange-600 transition-colors"
            style={{
              animation: 'slideInRight 0.3s ease-out'
            }}
            onClick={() => {
              // Find the node and open approval modal
              const node = nodes.find(n => n.id === notification.nodeId);
              if (node) {
                setApprovingNode(node);
                setIsApprovalModalOpen(true);
                // Dismiss notification
                setApprovalNotifications(prev =>
                  prev.filter(n => n.id !== notification.id)
                );
              }
            }}
            title="Click to approve this node"
          >
            <div className="flex-shrink-0 mt-1">
              <AlertTriangle size={24} className="animate-pulse" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-sm mb-1">Approval Required!</p>
              <p className="text-sm opacity-90">{notification.message}</p>
              <p className="text-xs opacity-75 mt-1">Click to approve</p>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();  // Prevent opening modal when clicking X
                setApprovalNotifications(prev =>
                  prev.filter(n => n.id !== notification.id)
                );
              }}
              className="flex-shrink-0 text-white hover:text-orange-200 transition-colors"
            >
              <XCircle size={20} />
            </button>
          </div>
        ))}
      </div>

      {/* Toast Notifications */}
      <div className="fixed top-4 right-4 z-50 space-y-2 max-w-md">
        {toastNotifications.map((toast) => {
          const bgColor = toast.type === 'error' ? 'bg-red-600' :
                          toast.type === 'warning' ? 'bg-yellow-500' :
                          'bg-green-600';
          const borderColor = toast.type === 'error' ? 'border-red-700' :
                              toast.type === 'warning' ? 'border-yellow-600' :
                              'border-green-700';
          return (
            <div
              key={toast.id}
              className={`${bgColor} text-white rounded-lg shadow-2xl p-4 flex items-start gap-3 border-2 ${borderColor}`}
              style={{ animation: 'slideInRight 0.3s ease-out' }}
            >
              <div className="flex-1">
                <p className="text-sm">{toast.message}</p>
              </div>
              <button
                onClick={() => dismissToast(toast.id)}
                className="flex-shrink-0 text-white hover:text-gray-200 transition-colors"
              >
                <XCircle size={18} />
              </button>
            </div>
          );
        })}
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          nodeType={contextMenu.nodeType}
          nodeData={contextMenu.nodeData}
          onCreateNode={() => { }} // Not used in workflow view
          onCreateAction={() => { }} // Not used in workflow view
          onAnalyzeWorkflow={() => { }} // Not used in workflow view
          onUploadAttachFile={handleUploadAttachFile}
          onSelectFromLibrary={handleSelectFromLibrary}
          onClose={() => setContextMenu(null)}
          autoClassify={false}
          autoSentiment={false}
        />
      )}

      {/* File Picker */}
      <FilePicker
        show={showFilePicker}
        onClose={() => setShowFilePicker(false)}
        onSelect={handleFileSelected}
        multiSelect={false}
      />

      {/* Files Viewer */}
      <FilesViewer
        workflow={{ ...initialWorkflow, id: workflowId }}
        nodes={nodes}
        isOpen={showFilesViewer}
        onClose={() => setShowFilesViewer(false)}
      />

      {/* Workflow Generated Files Browser */}
      {showWorkflowFiles && (
        <div className="fixed right-0 top-0 h-full w-80 bg-white shadow-xl border-l border-gray-200 z-40 flex flex-col animate-slide-in-right">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <Download size={16} className="text-purple-600" />
              Generated Files
            </h3>
            <button
              onClick={() => setShowWorkflowFiles(false)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              ✕
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {workflowFilesLoading ? (
              <div className="text-center text-gray-500 text-sm py-8">Loading files…</div>
            ) : workflowFiles.length === 0 ? (
              <div className="text-center text-gray-400 text-sm py-8">No files found</div>
            ) : (
              workflowFiles.map(f => (
                <div key={f.file_id} className="border border-gray-200 rounded-lg p-3 bg-gray-50 hover:bg-white transition-colors">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate" title={f.filename}>
                        {f.filename}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {f.mime_type} · {f.file_size ? `${Math.round(f.file_size / 1024)} KB` : '—'}
                      </div>
                      {f.workflow_node_key && (
                        <div className="text-xs text-purple-600 mt-0.5 font-mono">{f.workflow_node_key}</div>
                      )}
                      {f.file_id === status?.output_file_id && (
                        <span className="inline-block mt-1 text-xs bg-green-100 text-green-700 rounded px-1.5 py-0.5">Final Output</span>
                      )}
                    </div>
                    <button
                      onClick={() => handleDownloadFinalOutputFile(f.file_id, f.filename)}
                      className="flex-shrink-0 p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                      title="Download"
                    >
                      <Download size={15} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="p-3 border-t border-gray-200">
            <button
              onClick={handleLoadWorkflowFiles}
              className="w-full text-xs text-gray-500 hover:text-gray-700 transition-colors"
            >
              Refresh
            </button>
          </div>
        </div>
      )}

      {/* Toast Animation CSS */}
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in-right {
          animation: slideInRight 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

/**
 * Wrapper component with ReactFlowProvider
 */
export default function AIWorkflowVisualization(props) {
  return (
    <ReactFlowProvider>
      <AIWorkflowVisualizationInner {...props} />
    </ReactFlowProvider>
  );
}
